import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type ChangeEvent, type FC, useState } from 'react';
import { Controller, type FieldPath, useFormContext } from 'react-hook-form';

import { _get, _notNil, uuid } from '@/littledash';

import type { FormBuilderFieldOption, FormBuilderFormData } from '@/components/FormBuilder/FormBuilder.model';
import OptionsContainer from '@/components/FormBuilder/SelectBuilder/OptionsContainer';
import { hasOptionValues } from '@/components/FormBuilder/SelectBuilder/SelectBuilder.utils';
import SelectBuilderLabelInput from '@/components/FormBuilder/SelectBuilder/SelectBuilderLabelInput';
import SelectBuilderOption from '@/components/FormBuilder/SelectBuilder/SelectBuilderOption';
import AddButton from '@/components/UI/AddButton';
import RemoveButton from '@/components/UI/RemoveButton';

import './SelectBuilder.scss';

const createNewSelectOptionRow = (): FormBuilderFieldOption => ({
  label: '',
  value: '',
  clientRowId: uuid(),
});

interface SelectBuilderNames {
  label: FieldPath<FormBuilderFormData>;
  options: FieldPath<FormBuilderFormData>;
}

interface SelectBuilderDefaultValue {
  label: string;
  options: Array<FormBuilderFieldOption>;
}

interface SelectBuilderProps {
  names: SelectBuilderNames;
  defaultValue: SelectBuilderDefaultValue;
}

const SelectBuilder: FC<SelectBuilderProps> = ({ names, defaultValue }) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<FormBuilderFormData>();
  const [isExpanded, setIsExpanded] = useState(true);
  const [initialSelectOptions] = useState(() => {
    const raw = defaultValue.options ?? [createNewSelectOptionRow()];
    return raw.map((opt) => ({
      ...opt,
      clientRowId: opt.clientRowId ?? uuid(),
    }));
  });

  const toggleIsExpanded = () => {
    setIsExpanded((prevState) => !prevState);
  };

  const readOptionsFromForm = (): Array<FormBuilderFieldOption> | null => {
    const formValue = getValues(names.options);
    if (!Array.isArray(formValue)) {
      return null;
    }
    return formValue as Array<FormBuilderFieldOption>;
  };

  const handleSelectOptionChange = (
    optionValue: string,
    index: number,
    onChange: (v: Array<FormBuilderFieldOption>) => void
  ) => {
    const formValue = readOptionsFromForm();
    if (formValue === null) {
      return;
    }
    const newFieldOptions = formValue.map((opt, i) =>
      i === index ? { ...opt, label: optionValue, value: optionValue } : opt
    );
    onChange(newFieldOptions);
  };

  const onAddOption = (onChange: (v: Array<FormBuilderFieldOption>) => void) => {
    const formValue = readOptionsFromForm();
    if (formValue === null) {
      return;
    }
    onChange([...formValue, createNewSelectOptionRow()]);
  };

  const onRemoveOption = (indexToRemove: number, onChange: (v: Array<FormBuilderFieldOption>) => void) => {
    const formValue = readOptionsFromForm();
    if (formValue === null) {
      return;
    }
    const newFieldOptions = formValue.filter((_, i) => i !== indexToRemove);
    onChange(newFieldOptions);
  };

  const labelHasError = _notNil(_get(errors, names.label));

  return (
    <div className="flex flex-column" data-test-component="SelectBuilder" data-test-element="container">
      <Controller
        defaultValue={defaultValue.label ?? ''}
        name={names.label}
        rules={{
          required: 'A label is required',
        }}
        control={control}
        render={({ field: { onChange, value } }) => (
          <SelectBuilderLabelInput
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value);
            }}
            value={value}
            isExpanded={isExpanded}
            toggleIsExpanded={toggleIsExpanded}
            hasErrors={labelHasError}
          />
        )}
      />

      <Controller
        defaultValue={initialSelectOptions}
        name={names.options}
        rules={{
          required: 'Options are required',
          validate: {
            hasOptionValues,
          },
        }}
        control={control}
        render={({ field: { onChange, value } }) => {
          const optionsList: Array<FormBuilderFieldOption> = Array.isArray(value)
            ? (value as Array<FormBuilderFieldOption>)
            : [];
          return (
            <div
              className={cn('ml3 mb2', { dn: !isExpanded })}
              data-test-element="select-builder-option-list-container"
            >
              <div data-test-component="List" data-test-element="container">
                <OptionsContainer className="ui__repeater__children">
                  {optionsList.map((option, index) => (
                    <div
                      key={option.clientRowId ?? `select-option-${index}`}
                      className="flex flex-row items-start justify-between mb3 ui__repeater__child"
                    >
                      <div className="flex flex-row flex-auto">
                        <SelectBuilderOption
                          onChange={(v, idx) => {
                            handleSelectOptionChange(v, idx, onChange);
                          }}
                          index={index}
                          value={option.label}
                        />
                      </div>
                      {optionsList.length > 1 ? (
                        <RemoveButton
                          className="ml2"
                          onClick={() => {
                            onRemoveOption(index, onChange);
                          }}
                        />
                      ) : null}
                    </div>
                  ))}
                </OptionsContainer>
                <AddButton
                  className="mt3"
                  style={{ marginLeft: '2rem' }}
                  onClick={() => {
                    onAddOption(onChange);
                  }}
                >
                  Add option
                </AddButton>
              </div>
            </div>
          );
        }}
      />
      {Object.values(names).map((name) => (
        <ErrorMessage
          key={name}
          errors={errors}
          name={name}
          render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
        />
      ))}
    </div>
  );
};

export default SelectBuilder;
